/**
 * web/src/api/access.ts
 * Typed clients for /api/admin/access and /api/admin/users (admin redesign
 * §2.2). Server contract: src/api/admin/{access,users}.ts.
 *
 * Routes:
 *   GET  /api/admin/access  — fetchAccessMatrix() AdminAccessMatrix
 *   PUT  /api/admin/access  — toggleAccess({ userId, eventId, workflowType, granted })
 *   GET  /api/admin/users   — listAdminUsers() AdminUser[]
 *
 * Authorization: server requires admin role on every route. Non-admin
 * callers get a 403 ACCESS_DENIED translated into ApiClientError.
 */
import { api } from '../lib/api';
import type { AdminAccessMatrix, AdminUser, Role } from './types';

/** Body for PUT /api/admin/access — toggles one coarse (user, event) grant. */
export interface ToggleAccessBody {
  userId: string;
  eventId: string;
  granted: boolean;
}

/** Body for POST /api/admin/users/invite. */
export interface InviteUserBody {
  email: string;
  name: string;
  role: Role;
}

/**
 * Fetches the User Access matrix payload (users, events, workflow types,
 * grants) in a single round-trip.
 *
 * @returns AdminAccessMatrix
 * @throws ApiClientError(403) if not admin
 * @sideEffects HTTP GET
 * @idempotent yes
 */
export async function fetchAccessMatrix(): Promise<AdminAccessMatrix> {
  return api<AdminAccessMatrix>('/api/admin/access');
}

/**
 * Toggles a single (user, event) grant. `granted: true` upserts the row;
 * `granted: false` deletes it. Idempotent on the server side.
 *
 * @param body the grant coordinates + new state
 * @throws ApiClientError(403) if not admin; (404) if user/event missing
 * @sideEffects HTTP PUT
 * @idempotent yes — same body settles to the same final state
 */
export async function toggleAccess(body: ToggleAccessBody): Promise<void> {
  await api<void>('/api/admin/access', {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

/**
 * Lists the full user roster. Used as a standalone fetch when the matrix
 * isn't needed (e.g., user picker components in future flows).
 *
 * @returns AdminUser[]
 * @throws ApiClientError(403) if not admin
 * @sideEffects HTTP GET
 * @idempotent yes
 */
export async function listAdminUsers(): Promise<AdminUser[]> {
  return api<AdminUser[]>('/api/admin/users');
}

/**
 * Phase 6.2 SPN-17: sets or clears User.slackUserId. Used by the User
 * Access page's Slack User ID column. The 48h follow-up scheduler reads
 * this field when posting the reminder to mention the original approver.
 *
 * Body shape: `{ slackUserId: 'U02ABCXYZ12' | null }` (null clears).
 *
 * @param userId target User.id
 * @param slackUserId Slack workspace member ID, or null to clear
 * @throws ApiClientError(403) if not admin; (400) on malformed ID; (404) on missing user
 * @sideEffects HTTP PATCH
 * @idempotent yes — same body settles to the same final state
 */
export async function patchUserSlackUserId(
  userId: string,
  slackUserId: string | null,
): Promise<void> {
  await api<void>(`/api/admin/users/${userId}/slack-user-id`, {
    method: 'PATCH',
    body: JSON.stringify({ slackUserId }),
  });
}

/**
 * Updates a user's role. Server validates the enum.
 *
 * @param userId target User.id
 * @param role one of 'sales_ops' | 'content' | 'operations' | 'admin'
 * @throws ApiClientError(403) if not admin; (400) on self-demotion away from admin; (404) missing user
 * @sideEffects HTTP PATCH
 * @idempotent yes — same role settles to same final state
 */
export async function patchUserRole(
  userId: string,
  role: 'sales_ops' | 'content' | 'operations' | 'admin',
): Promise<void> {
  await api<void>(`/api/admin/users/${userId}/role`, {
    method: 'PATCH',
    body: JSON.stringify({ role }),
  });
}

/**
 * Hard-deletes a user. Cascades to Session + UserEventAccess rows. Forensic
 * string references on Workflow / TrackedEmail / AuditEntry are preserved
 * (the display name remains; the user id becomes dangling).
 *
 * @param userId target User.id
 * @throws ApiClientError(403) if not admin; (400) when deleting yourself; (404) missing user
 * @sideEffects HTTP DELETE
 * @idempotent no — re-deleting throws 404
 */
export async function deleteAdminUser(userId: string): Promise<void> {
  await api<void>(`/api/admin/users/${userId}`, { method: 'DELETE' });
}

/**
 * Pre-creates a user row so Google SSO matches them on first sign-in (the
 * OAuth callback is whitelist-only — `findUnique({ where: { email } })` with
 * 403 ACCESS_DENIED on miss). No email is sent. Server returns the created
 * row in the same projection as GET /api/admin/users.
 *
 * @param body new user payload (email + name + role)
 * @returns the persisted AdminUser
 * @throws ApiClientError(403) if not admin; (400) validation; (409) duplicate email
 * @sideEffects HTTP POST
 * @idempotent no — second call with the same email returns 409
 */
export async function inviteUser(body: InviteUserBody): Promise<AdminUser> {
  return api<AdminUser>('/api/admin/users/invite', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}
